import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SUPPORTED_LOCALES, type Locale } from '@/env'
import { useLocaleNavigate } from '@/lib/localePath'

/** Native language names — kept for the account-settings picker in Profile.
 *  The header switcher itself shows only the 2-letter code. */
export const LOCALE_NAMES: Record<Locale, string> = {
  es: 'Español',
  ca: 'Català',
  eu: 'Euskara',
  gl: 'Galego',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  pt: 'Português',
}

/**
 * Reflects the choice in the URL prefix (so a refresh keeps the locale and the
 * route stays locale-prefixed) via SPA navigation — no page reload, so open
 * dialogs and scroll position survive the switch.
 *
 * Shown only on the landing/public header: language is picked once, then fixed
 * for the session. Rendered as a compact, neutral 2-letter code pill — no brand
 * accent, since this isn't an action you change often.
 */
export function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const navigateLocale = useLocaleNavigate()
  const current = (i18n.resolvedLanguage ?? i18n.language ?? 'en') as Locale

  return (
    <Select value={current} onValueChange={navigateLocale}>
      <SelectTrigger
        className="h-9 w-14 justify-center rounded-full border border-border bg-muted/50 p-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label={LOCALE_NAMES[current] ?? current}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SUPPORTED_LOCALES.map((l) => (
          <SelectItem key={l} value={l} className="uppercase">
            {l.toUpperCase()}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}